import classNames from 'classnames';
import TextField from 'components/fields/text-field';
import Hero from 'components/heroes/hero';
import Page from 'components/page/page';
import Text from 'components/text/text';
import TransitionLink from 'components/transition-link/transition-link';
import Endpoints from 'helpers/enums/endpoints';
import MakeHash from 'make-hash';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { loadScriptAsync } from 'src/client/pages/auth/gigya';
import getParamByName from 'utils/get-param-by-name';
import { gigyaLogin } from './gigya';
import messages from './messages';

class SignIn extends Component {
    constructor(props) {
        super(props);

        const { intl } = props;

        this.state = {
            serverError: false,
            formMessages: [],
            loading: false,
            userFetchError:
                getParamByName('error', props.history.location.search) ===
                'userFetch',
            acceptTOU: false,
            acceptTAC: true,
            showTermsCheckboxes: false,
        };
        this.page = {
            heading: intl.formatMessage(messages.sign_in_heading),
            intro: intl.formatMessage(messages.sign_in_intro),
            loginError: intl.formatMessage(messages.sign_in_error),
        };
        this.primaryAction = {
            path: '/',
            label: intl.formatMessage(messages.sign_in_label),
        };
        this.secondaryAction = {
            path: '/forgot-password',
            label: intl.formatMessage(messages.forgot_password_label),
        };

        this.fields = [];

        this.setUpHandlers();
    }

    /**
     * Binds any handlers to the correct context
     *
     * @method  setUpHandlers
     */
    setUpHandlers() {
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleError = this.handleError.bind(this);
    }

    /**
     * React Lifecycle Hook
     * Invoked once, immediately after the initial rendering occurs.
     *
     * @method  componentDidMount
     */
    componentDidMount() {
        this.fields = [this.email, this.password];

        // Load gigya screen set if gigya env configured
        if (process.env.REACT_APP_AUTH_BACKEND === 'gigya') {
            loadScriptAsync(
                `https://cdns.gigya.com/js/gigya.js?apikey=${process.env.REACT_APP_GIGYA_KEY}`
            ).then(() => {
                gigyaLogin();
            });
        }
    }

    handleSubmit(event) {
        const isValid = !this.formHasErrors();

        if (event) event.preventDefault();

        if (isValid) {
            this.setState({
                serverError: false,
                formMessages: [],
            });
            this.login();
        }
    }

    formHasErrors() {
        let errors = this.fields.filter((f) => {
            return !f.isValid();
        });

        return errors.length > 0;
    }

    handleError(error) {
        this.setState({
            loading: false,
        });

        error.response.json().then((errorBody) => {
            // Try to translate first error
            // EMAIL_NOT_VERIFIED, or USER_NOT_ACCEPTED_TERMS or INVALID_USERNAME_OR_PASSWORD
            if (errorBody.errors) {
                const firstError = errorBody.errors[0];
                const message = messages[firstError.message];

                let showTerms = false;
                if (firstError.message === 'USER_NOT_ACCEPTED_TERMS') {
                    showTerms = true;
                }

                // errors may look like  so handle if they are present in our messages
                if (message) {
                    return this.setState({
                        showTermsCheckboxes: showTerms,
                        serverError: true,
                        formMessages: [this.props.intl.formatMessage(message)],
                    });
                }
            }

            // Use reason from the server if were unable to find error enum in our locales
            if (errorBody.reason) {
                return this.setState({
                    serverError: true,
                    formMessages: [errorBody.reason],
                });
            }

            // Use untranslated "non_field_errors" if that is present
            if (errorBody.non_field_errors) {
                return this.setState({
                    serverError: true,
                    formMessages: errorBody.non_field_errors,
                });
            }

            // Use a final catch-all error if none of the above applied
            this.setState({
                serverError: true,
                formMessages: ['Uknown error. Please try again.'],
            });
        });
    }

    /**
     * Attempts to log a user in via Auth API
     *
     * @method  login
     */
    login() {
        const email = this.email.input.value;
        const password = this.password.input.value;

        this.setState({ loading: true });

        const body = { email: email, password: password, client: 'rider' };

        // Include agreements checkboxes if applicable
        // if (this.state.acceptTAC) {
        //     body.tac = true;
        // }

        if (this.state.acceptTOU) {
            body.tou = true;
        }

        this.props.apiService
            .post(Endpoints.login, body, this.handleError)
            .then((data) => {
                if (typeof data === 'undefined') return;

                this.props.authService.setToken(data.token);

                this.setState({ loading: false }, () => {
                    this.props.setAuthState(true, () => {
                        this.props.history.replace(this.primaryAction.path);
                    });
                });
            });
    }

    _renderFormMessages() {
        const formErrorsClasses = classNames('form-messages', {
            'form-messages_hasMessage': this.state.formMessages.length > 0,
        });
        const color = this.state.serverError ? 'DefaultOrange' : 'DefaultBlue';

        return (
            <ul className={formErrorsClasses}>
                {this.state.formMessages.map((msg, i) => {
                    return (
                        <li key={MakeHash(`${msg}-${i}`)}>
                            <Text color={color} weight="light">
                                {msg.endsWith('.') ? msg : msg + '.'}
                            </Text>
                        </li>
                    );
                })}
            </ul>
        );
    }

    _renderHero() {
        const { intl } = this.props;
        const supportingTxt = this.state.userFetchError
            ? this.page.loginError
            : this.page.intro;
        const heroAction = !this.state.userFetchError ? (
            ''
        ) : (
            <TransitionLink
                className="btn btn_dark btn_fixedWidth"
                to="https://www.retul.com/contact/"
                target="_blank"
            >
                {intl.formatMessage(messages.contact_label)}
            </TransitionLink>
        );

        return (
            <Hero
                title={this.page.heading}
                supportingTxt={supportingTxt}
                serverError={this.state.userFetchError}
            >
                {heroAction}
            </Hero>
        );
    }

    render() {
        const { intl } = this.props;
        const pageHero = this._renderHero();
        const formMessages = this._renderFormMessages();

        // TODO: remove userScreensDiv along with gigya when the switch happens
        // div id="userScreensDiv" is where gigya screensets are appended
        const className =
            process.env.REACT_APP_AUTH_BACKEND === 'gigya' ? 'hidden' : '';

        const checkboxClasses = classNames('field field_floatingLabel', {
            hidden: !this.state.showTermsCheckboxes,
        });

        return (
            <Page
                fixedLoader
                isLoading={this.state.loading}
                title={this.page.heading}
                pageHero={pageHero}
            >
                <div className="container container_constrainedMd">
                    <div className="gigya-screen-set" id="userScreensDiv" />
                    <form
                        onSubmit={this.handleSubmit}
                        autoComplete="off"
                        noValidate
                        id="signin-form"
                        className={className}
                    >
                        {formMessages}
                        <div className="form-fields">
                            <div className="set">
                                <div className="set-hd">
                                    <TextField
                                        id="email"
                                        name="email"
                                        type="email"
                                        label={intl.formatMessage(
                                            messages.email_label
                                        )}
                                        errorMsg={intl.formatMessage(
                                            messages.email_format_error
                                        )}
                                        ref={(field) => {
                                            this.email = field;
                                        }}
                                    />
                                </div>
                                <div className="set-bd set-bd_super">
                                    <TextField
                                        id="password"
                                        name="password"
                                        type="password"
                                        label={intl.formatMessage(
                                            messages.password_label
                                        )}
                                        errorMsg={intl.formatMessage(
                                            messages.password_invalid_error
                                        )}
                                        ref={(field) => {
                                            this.password = field;
                                        }}
                                    />
                                </div>
                                <br />
                                <div className={checkboxClasses}>
                                    <input
                                        id="accept-tou"
                                        name="accept terms of use"
                                        type="checkbox"
                                        value={this.state.acceptTOU}
                                        onChange={(e) => {
                                            this.setState({
                                                acceptTOU: !this.state
                                                    .acceptTOU,
                                            });
                                        }}
                                    />
                                    <label htmlFor="accept-tou">
                                        <a
                                            target="_blank"
                                            className="txt txt_reg mix-txt_colorDefaultBlue"
                                            href="https://www.specialized.com/us/en/terms-of-use"
                                        >
                                            {intl.formatMessage(
                                                messages.accept_tou_label
                                            )}
                                        </a>
                                    </label>
                                </div>
                                <div className={checkboxClasses}>
                                    <label htmlFor="accept-privacy-policy">
                                        {intl.formatMessage(
                                            messages.accept_privacy_policy_sign_in_label
                                        )}
                                        &nbsp;
                                        <a
                                            target="_blank"
                                            className="txt txt_reg mix-txt_colorDefaultBlue"
                                            href="https://www.specialized.com/privacy-policy"
                                        >
                                            {intl.formatMessage(
                                                messages.accept_privacy_policy_link_text
                                            )}
                                        </a>
                                    </label>
                                </div>

                                <div className="set-bd set-bd_super">
                                    <div className="set mix-set_alignCenter">
                                        <div className="set-hd">
                                            <button
                                                type="submit"
                                                id="signin-submit"
                                                className="btn btn_fixedWidth"
                                            >
                                                {this.primaryAction.label}
                                            </button>
                                        </div>

                                        <div className="set-bd set-bd_separated">
                                            <TransitionLink
                                                className="txt txt_reg mix-txt_colorDefaultBlue"
                                                to="/create-account"
                                            >
                                                {intl.formatMessage(
                                                    messages.create_account_label
                                                )}
                                            </TransitionLink>
                                        </div>

                                        <div className="set-bd set-bd_separated">
                                            <TransitionLink
                                                className="txt txt_reg mix-txt_colorDefaultBlue"
                                                to={this.secondaryAction.path}
                                            >
                                                {this.secondaryAction.label}
                                            </TransitionLink>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </Page>
        );
    }
}

export default injectIntl(SignIn);



// WEBPACK FOOTER //
// ./src/client/pages/auth/sign-in.jsx